import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  ArrowRight, ArrowLeft, Home, LayoutGrid, 
  Clock, Square, Minus, Plus, Repeat, 
  CheckCircle, Pause, Play, Trash2, Gift, Copy,
  TrendingDown, CalendarDays, Lock, MapPin, Mail, BadgePercent,
  User, Phone, Eye, EyeOff
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Tarifs de base pour affichage - COHERENTS
const TARIFS_BASE = {
  ponctuel: { heure: 36, m2: 3.00, reduction: 0 },
  quotidien: { heure: 30, m2: 2.50, reduction: 17 },
  hebdomadaire: { heure: 32, m2: 2.65, reduction: 11 },
  bi_mensuel: { heure: 34, m2: 2.80, reduction: 6 },
  mensuel: { heure: 35, m2: 2.90, reduction: 3 },
};

const JOURS_SEMAINE = [
  { value: "lundi", label: "Lundi" },
  { value: "mardi", label: "Mardi" },
  { value: "mercredi", label: "Mercredi" },
  { value: "jeudi", label: "Jeudi" },
  { value: "vendredi", label: "Vendredi" },
  { value: "samedi", label: "Samedi" },
];

const HEURES = ["08:00", "09:00", "10:00", "11:00", "14:00", "15:00", "16:00", "17:00"];

export const AbonnementFlow = () => {
  const navigate = useNavigate();
  const { user, getToken, login, register } = useAuth();
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [mesAbonnements, setMesAbonnements] = useState([]);
  const [showForm, setShowForm] = useState(true);
  
  // Form state
  const [selectedService, setSelectedService] = useState(null);
  const [menageMode, setMenageMode] = useState("heures");
  const [menageValeur, setMenageValeur] = useState("");
  const [frequence, setFrequence] = useState("hebdomadaire");
  const [typeRecurrence, setTypeRecurrence] = useState("jour_semaine");
  const [jourSemaine, setJourSemaine] = useState("jeudi");
  const [jourMois, setJourMois] = useState(15);
  const [heure, setHeure] = useState("10:00");
  const [adresse, setAdresse] = useState("");
  const [codePostal, setCodePostal] = useState("");
  const [ville, setVille] = useState("");
  const [message, setMessage] = useState("");

  // Inline auth for non-logged users
  const [authData, setAuthData] = useState({
    nom: "", prenom: "", email: "", telephone: "", password: ""
  });
  const [showInlineAuth, setShowInlineAuth] = useState(false);
  const [isReturningUser, setIsReturningUser] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Vérifier si le code postal est dans le département 34 (Hérault)
  const isCodePostalHerault = (cp) => {
    return cp && cp.length >= 2 && cp.startsWith("34");
  };

  const codePostalHorsZone = codePostal && codePostal.length >= 2 && !isCodePostalHerault(codePostal);

  const services = [
    { id: "menage", icon: Home, label: "Ménage régulier", desc: "Entretien de votre domicile" },
    { id: "vitres", icon: LayoutGrid, label: "Nettoyage vitres", desc: "Vitres et encadrements" },
  ];

  const frequences = [
    { value: "quotidien", label: "Quotidien", desc: "Tous les jours", popular: false },
    { value: "hebdomadaire", label: "Hebdomadaire", desc: "Une fois par semaine", popular: true },
    { value: "bi_mensuel", label: "Bi-mensuel", desc: "2 fois par mois", popular: false },
    { value: "mensuel", label: "Mensuel", desc: "Une fois par mois", popular: false },
  ];

  const typesRecurrence = [
    { value: "jour_semaine", label: "Un jour de la semaine", desc: "Ex: tous les jeudis" },
    { value: "jour_mois", label: "Un jour du mois", desc: "Ex: le 15 de chaque mois" },
    { value: "premier_jour", label: "Le premier... du mois", desc: "Ex: le 1er mercredi" },
    { value: "dernier_jour", label: "Le dernier... du mois", desc: "Ex: le dernier vendredi" },
  ];

  // Load pending reservation from localStorage
  useEffect(() => {
    const pending = localStorage.getItem('pendingReservation');
    if (pending) {
      const data = JSON.parse(pending);
      if (data.mode === 'abonnement' && data.abonnementData) {
        const r = data.abonnementData;
        setSelectedService(r.selectedService);
        setMenageMode(r.menageMode || "heures");
        setMenageValeur(r.menageValeur || "");
        setFrequence(r.frequence || "hebdomadaire");
        setTypeRecurrence(r.typeRecurrence || "jour_semaine");
        setJourSemaine(r.jourSemaine || "jeudi");
        setJourMois(r.jourMois || 15);
        setHeure(r.heure || "10:00");
        setAdresse(r.adresse || "");
        setCodePostal(r.codePostal || "");
        setVille(r.ville || "");
        setMessage(r.message || "");
        setStep(r.step || 1);
        setShowForm(true);
        
        // If user just logged in, auto-submit
        if (user && data.autoSubmit) {
          setTimeout(() => {
            submitAbonnement();
          }, 500);
        }
      }
    }
  }, [user]);

  // Pre-fill address from user profile if logged in
  useEffect(() => {
    if (user) {
      if (!adresse) setAdresse(user.adresse || "");
      if (!codePostal) setCodePostal(user.code_postal || "");
      if (!ville) setVille(user.ville || "");
      fetchAbonnements();
    }
  }, [user]);

  const fetchAbonnements = async () => {
    if (!user) return;
    try {
      const res = await axios.get(`${API}/mes-abonnements`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setMesAbonnements(res.data);
      // If user has abonnements, show list first
      if (res.data.length > 0) {
        setShowForm(false);
      }
    } catch (e) {
      console.error(e);
    }
  };

  // Calculate price
  const calculatePrice = () => {
    if (!selectedService || selectedService !== "menage") return null;
    
    const val = parseFloat(menageValeur) || 0;
    if (val <= 0) return null;
    
    const tarifs = TARIFS_BASE[frequence] || TARIFS_BASE.ponctuel;
    const tarifBase = TARIFS_BASE.ponctuel;
    
    const tarifUnitaire = menageMode === "m2" ? tarifs.m2 : tarifs.heure;
    const tarifBaseUnitaire = menageMode === "m2" ? tarifBase.m2 : tarifBase.heure;
    
    const prix = val * tarifUnitaire;
    const prixBase = val * tarifBaseUnitaire;
    const economie = prixBase - prix;
    
    return {
      tarifUnitaire,
      prix: prix.toFixed(2),
      prixApresCredit: (prix * 0.5).toFixed(2),
      reduction: tarifs.reduction,
      economie: economie.toFixed(2)
    };
  };

  const submitAbonnement = async () => {
    if (!adresse || !heure) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }
    
    setLoading(true);
    try {
      const details = {
        mode: menageMode,
        valeur: parseFloat(menageValeur) || 0
      };

      const payload = {
        type_prestation: selectedService,
        frequence,
        type_recurrence: typeRecurrence,
        jour_semaine: ["jour_semaine", "premier_jour", "dernier_jour"].includes(typeRecurrence) ? jourSemaine : null,
        jour_mois: typeRecurrence === "jour_mois" ? jourMois : null,
        heure,
        adresse,
        code_postal: codePostal,
        ville,
        details_prestation: details,
        message
      };

      const res = await axios.post(`${API}/abonnement`, payload, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      
      // Clear pending
      localStorage.removeItem('pendingReservation');
      
      toast.success("Abonnement créé avec succès !", {
        description: `Prochaine intervention : ${new Date(res.data.prochaine_intervention).toLocaleDateString('fr-FR')}`
      });
      
      // Reset and show list
      setShowForm(false);
      setStep(1);
      setSelectedService(null);
      setMenageValeur("");
      fetchAbonnements();
    } catch (error) {
      toast.error("Erreur lors de la création de l'abonnement");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!adresse || !heure) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }
    
    if (!user) {
      setShowInlineAuth(true);
      setTimeout(() => {
        document.getElementById('inline-auth-section-abo')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
      return;
    }
    
    await submitAbonnement();
  };

  const handleInlineRegisterAndSubmit = async () => {
    if (!isReturningUser) {
      if (!authData.nom || !authData.prenom || !authData.email || !authData.telephone || !authData.password) {
        toast.error("Veuillez remplir tous les champs");
        return;
      }
      if (authData.password.length < 6) {
        toast.error("Le mot de passe doit contenir au moins 6 caractères");
        return;
      }
    } else {
      if (!authData.email || !authData.password) {
        toast.error("Veuillez remplir votre email et mot de passe");
        return;
      }
    }

    setLoading(true);
    try {
      if (isReturningUser) {
        await login(authData.email, authData.password);
      } else {
        await register({
          nom: authData.nom,
          prenom: authData.prenom,
          email: authData.email,
          telephone: authData.telephone,
          password: authData.password,
          adresse: adresse,
          code_postal: codePostal,
          ville: ville
        });
      }
      
      toast.success(isReturningUser ? "Connexion réussie !" : "Compte créé !");
      
      setTimeout(async () => {
        try {
          const token = localStorage.getItem("iks_token");
          const tarifBase = TARIFS_BASE[frequence];
          const tarifUnitaire = menageMode === "m2" ? tarifBase.m2 : tarifBase.heure;
          const valNum = parseFloat(menageValeur) || 0;
          let prixPrestation = 0;
          
          if (selectedService === "menage") {
            prixPrestation = valNum * tarifUnitaire;
          } else if (selectedService === "vitres") {
            prixPrestation = valNum * tarifUnitaire;
          }
          
          const prixApresCredit = prixPrestation * 0.5;

          await axios.post(`${API}/abonnement`, {
            type_prestation: selectedService,
            frequence: frequence,
            type_recurrence: typeRecurrence,
            jour_semaine: ["jour_semaine", "premier_jour", "dernier_jour"].includes(typeRecurrence) ? jourSemaine : null,
            jour_mois: typeRecurrence === "jour_mois" ? jourMois : null,
            heure: heure,
            adresse: adresse,
            code_postal: codePostal,
            ville: ville,
            details_prestation: {
              service: selectedService,
              mode: menageMode,
              valeur: valNum
            },
            message: message
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          localStorage.removeItem('pendingReservation');
          toast.success("Abonnement créé avec succès !");
          navigate("/particuliers");
        } catch (error) {
          const detail = error.response?.data?.detail;
          const msg = typeof detail === 'string' ? detail : "Erreur lors de la création de l'abonnement";
          toast.error(msg);
        } finally {
          setLoading(false);
        }
      }, 500);
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : "Erreur lors de la création du compte";
      toast.error(msg);
      setLoading(false);
    }
  };

  const handlePause = async (id) => {
    try {
      await axios.post(`${API}/abonnement/${id}/pause`, {}, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success("Abonnement mis en pause");
      fetchAbonnements();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const handleReprendre = async (id) => {
    try {
      await axios.post(`${API}/abonnement/${id}/reprendre`, {}, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success("Abonnement repris !");
      fetchAbonnements();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const handleAnnuler = async (id) => {
    if (!confirm("Êtes-vous sûr de vouloir annuler cet abonnement ?")) return;
    try {
      await axios.delete(`${API}/abonnement/${id}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success("Abonnement annulé");
      fetchAbonnements();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const price = calculatePrice();

  const abonnementsActifs = mesAbonnements.filter(a => a.statut === "actif");
  const abonnementsPause = mesAbonnements.filter(a => a.statut === "pause");

  return (
    <div className="min-h-screen bg-gray-50" data-testid="abonnement-flow">
      <div className="max-w-6xl mx-auto px-6 py-12">
        
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-[#003264] mb-1">
              Abonnement régulier
            </h1>
            <p className="text-gray-500">Prestations régulières avec tarifs préférentiels</p>
          </div>
          {user && !showForm && (
            <Button 
              onClick={() => setShowForm(true)}
              className="bg-[#003264] hover:bg-[#002244] text-white"
            >
              <Repeat className="w-4 h-4 mr-2" />
              Nouvel abonnement
            </Button>
          )}
        </div>

        {/* Tarifs dégressifs banner */}
        <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-2xl p-6 mb-8">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
              <TrendingDown className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">Tarifs dégressifs + Avance immédiate</h3>
              <p className="text-sm text-gray-600">Combinez la réduction abonnement et le crédit d'impôt -50% !</p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {frequences.map(f => {
              const tarif = TARIFS_BASE[f.value];
              const prixApresCredit = (tarif.heure / 2).toFixed(0);
              return (
                <div key={f.value} className={`relative rounded-xl p-4 text-center ${
                  f.popular 
                    ? "bg-[#003264] text-white ring-2 ring-[#003264] ring-offset-2" 
                    : "bg-white"
                }`} data-testid={`tarif-card-${f.value}`}>
                  {f.popular && (
                    <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-3 py-0.5 bg-amber-400 text-amber-900 text-[10px] font-bold rounded-full uppercase tracking-wider whitespace-nowrap">
                      Le + choisi
                    </span>
                  )}
                  <p className={`text-xs font-medium mb-1 ${f.popular ? "text-white/70" : "text-gray-500"}`}>{f.label}</p>
                  <p className={`text-sm line-through ${f.popular ? "text-white/40" : "text-gray-400"}`}>{tarif.heure}€/h</p>
                  <p className={`text-xl font-black ${f.popular ? "text-white" : "text-emerald-600"}`}>{prixApresCredit}€<span className="text-xs font-normal">/h</span></p>
                  <div className="flex items-center justify-center gap-1 mt-1 flex-wrap">
                    {tarif.reduction > 0 && (
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                        f.popular ? "bg-white/20 text-white" : "bg-emerald-100 text-emerald-700"
                      }`}>
                        -{tarif.reduction}%
                      </span>
                    )}
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                      f.popular ? "bg-white/20 text-white" : "bg-emerald-100 text-emerald-700"
                    }`}>
                      -50% crédit
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Form or List */}
        {showForm ? (
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Form */}
            <div className="lg:col-span-2 space-y-6">
              
              {/* Step 1: Service + Fréquence */}
              <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                <div className="bg-[#003264] text-white p-4 flex items-center gap-3">
                  <span className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center text-sm font-bold">1</span>
                  <span className="font-semibold">Service et fréquence</span>
                </div>
                
                {step === 1 && (
                  <div className="p-6 space-y-6">
                    {/* Service selection */}
                    <div>
                      <Label className="text-[#003264] font-semibold mb-3 block">Type de prestation</Label>
                      <div className="grid gap-3">
                        {services.map(service => {
                          const isSelected = selectedService === service.id;
                          return (
                            <button
                              key={service.id}
                              onClick={() => setSelectedService(service.id)}
                              className={`p-4 rounded-xl border-2 text-left transition-all flex items-center gap-4 ${
                                isSelected 
                                  ? "border-[#003264] bg-blue-50" 
                                  : "border-gray-200 hover:border-gray-300"
                              }`}
                            >
                              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                                isSelected ? "bg-[#003264] text-white" : "bg-gray-100 text-gray-500"
                              }`}>
                                <service.icon className="w-6 h-6" />
                              </div>
                              <div className="flex-1">
                                <p className="font-semibold text-gray-900">{service.label}</p>
                                <p className="text-sm text-gray-500">{service.desc}</p>
                              </div>
                              {isSelected && (
                                <CheckCircle className="w-6 h-6 text-[#003264]" />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Ménage config */}
                    {selectedService === "menage" && (
                      <div className="bg-gray-50 rounded-xl p-4">
                        <Label className="text-gray-700 font-medium mb-3 block">Configuration</Label>
                        <div className="flex gap-2 mb-4">
                          <button
                            onClick={() => setMenageMode("heures")}
                            className={`flex-1 py-3 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                              menageMode === "heures" ? "bg-[#003264] text-white" : "bg-white border text-gray-600"
                            }`}
                          >
                            <Clock className="w-4 h-4" /> À l'heure
                          </button>
                          <button
                            onClick={() => setMenageMode("m2")}
                            className={`flex-1 py-3 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                              menageMode === "m2" ? "bg-[#003264] text-white" : "bg-white border text-gray-600"
                            }`}
                          >
                            <Square className="w-4 h-4" /> Au m²
                          </button>
                        </div>
                        <Input
                          type="number"
                          min="1"
                          placeholder={menageMode === "m2" ? "Surface en m²" : "Nombre d'heures"}
                          value={menageValeur}
                          onChange={(e) => setMenageValeur(e.target.value)}
                          className="h-12 text-lg"
                        />
                      </div>
                    )}

                    {/* Fréquence */}
                    <div>
                      <Label className="text-[#003264] font-semibold mb-3 block">Fréquence</Label>
                      <div className="grid grid-cols-2 gap-3">
                        {frequences.map(f => {
                          const isSelected = frequence === f.value;
                          const tarif = TARIFS_BASE[f.value];
                          return (
                            <button
                              key={f.value}
                              onClick={() => setFrequence(f.value)}
                              className={`relative p-4 rounded-xl border-2 text-left transition-all ${
                                isSelected 
                                  ? "border-[#003264] bg-blue-50" 
                                  : f.popular
                                  ? "border-emerald-300 bg-emerald-50/50 hover:border-emerald-400"
                                  : "border-gray-200 hover:border-gray-300"
                              }`}
                              data-testid={`freq-${f.value}`}
                            >
                              {f.popular && (
                                <span className="absolute -top-2.5 left-3 px-2 py-0.5 bg-emerald-500 text-white text-[10px] font-bold rounded-full uppercase tracking-wider">
                                  Le + choisi
                                </span>
                              )}
                              <div className="flex items-center justify-between mb-1">
                                <p className={`font-semibold ${f.popular && !isSelected ? "text-emerald-800" : "text-gray-900"}`}>{f.label}</p>
                                {tarif.reduction > 0 && (
                                  <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full">
                                    -{tarif.reduction}%
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-gray-500">{f.desc}</p>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <Button
                      onClick={() => setStep(2)}
                      disabled={!selectedService || (selectedService === "menage" && !menageValeur)}
                      className="w-full bg-[#003264] hover:bg-[#002244] text-white"
                    >
                      Continuer <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                )}

                {step > 1 && (
                  <div className="p-4 bg-gray-50 border-t flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Home className="w-5 h-5 text-[#003264]" />
                      <span className="font-medium text-gray-900">
                        {services.find(s => s.id === selectedService)?.label}
                      </span>
                      <span className="text-gray-500">•</span>
                      <span className="text-gray-600">{frequences.find(f => f.value === frequence)?.label}</span>
                    </div>
                    <button onClick={() => setStep(1)} className="text-sm text-[#003264] hover:underline">
                      Modifier
                    </button>
                  </div>
                )}
              </div>

              {/* Step 2: Planning */}
              {step >= 2 && (
                <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                  <div className="bg-[#003264] text-white p-4 flex items-center gap-3">
                    <span className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center text-sm font-bold">2</span>
                    <span className="font-semibold">Jour et heure</span>
                  </div>
                  
                  <div className="p-6 space-y-5">
                    {/* Choix du jour - simplifié */}
                    {frequence === "quotidien" ? (
                      <div className="bg-emerald-50 rounded-xl p-4 text-center">
                        <p className="text-emerald-700 font-medium">
                          Intervention tous les jours du lundi au samedi
                        </p>
                      </div>
                    ) : (
                      <div>
                        <Label className="text-gray-700 font-medium mb-3 block">
                          {frequence === "hebdomadaire" && "Quel jour de la semaine ?"}
                          {frequence === "bi_mensuel" && "Quel jour pour vos 2 interventions mensuelles ?"}
                          {frequence === "mensuel" && "Quel jour pour votre intervention mensuelle ?"}
                        </Label>
                        <div className="grid grid-cols-3 gap-2">
                          {JOURS_SEMAINE.map(j => (
                            <button
                              key={j.value}
                              onClick={() => setJourSemaine(j.value)}
                              className={`py-3 px-4 rounded-xl text-center font-medium transition-all ${
                                jourSemaine === j.value
                                  ? "bg-[#003264] text-white"
                                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                              }`}
                            >
                              {j.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Heure - simplifié avec boutons */}
                    <div>
                      <Label className="text-gray-700 font-medium mb-3 block">À quelle heure ?</Label>
                      <div className="grid grid-cols-4 gap-2">
                        {HEURES.map(h => (
                          <button
                            key={h}
                            onClick={() => setHeure(h)}
                            className={`py-3 rounded-xl text-center font-medium transition-all ${
                              heure === h
                                ? "bg-[#003264] text-white"
                                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                            }`}
                          >
                            {h}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Adresse */}
                    <div>
                      <Label className="text-gray-700 font-medium mb-3 block">Adresse d'intervention *</Label>
                      <Input 
                        value={adresse} 
                        onChange={(e) => setAdresse(e.target.value)} 
                        placeholder="Rue"
                        className="h-12 mb-2"
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <Input 
                          value={codePostal} 
                          onChange={(e) => setCodePostal(e.target.value)} 
                          placeholder="Code postal"
                          maxLength={5}
                          className="h-12"
                        />
                        <Input 
                          value={ville} 
                          onChange={(e) => setVille(e.target.value)} 
                          placeholder="Ville"
                          className="h-12"
                        />
                      </div>
                    </div>

                    {/* Message */}
                    <div>
                      <Label className="text-gray-700 font-medium mb-3 block">Instructions (optionnel)</Label>
                      <Textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Digicode, étage, clé sous le paillasson..."
                        rows={3}
                      />
                    </div>

                    {/* Si hors zone 34 - Message contact */}
                    {codePostalHorsZone ? (
                      <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-5 text-center">
                        <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
                          <MapPin className="w-6 h-6 text-amber-600" />
                        </div>
                        <h4 className="font-bold text-[#003264] mb-2">Zone non couverte</h4>
                        <p className="text-sm text-gray-600 mb-4">
                          Votre adresse se situe hors de notre zone d'intervention habituelle (Hérault - 34).
                          <br />Pour un abonnement hors zone, un <strong>devis personnalisé</strong> est nécessaire.
                        </p>
                        <a
                          href="mailto:contact@iks-services.fr?subject=Demande abonnement hors zone 34&body=Bonjour,%0A%0AJe souhaite souscrire un abonnement pour une adresse située hors du département 34 :%0A%0ACode postal : "
                          className="inline-flex items-center justify-center gap-2 w-full py-3 bg-[#003264] text-white rounded-lg hover:bg-[#002244] transition font-medium"
                        >
                          <Mail className="w-4 h-4" />
                          Demander un devis par email
                        </a>
                        <p className="text-xs text-gray-500 mt-3">
                          Ou appelez-nous au <strong>06 25 16 80 27</strong>
                        </p>
                      </div>
                    ) : (
                      <>
                        {/* Inline Auth Form for non-logged users */}
                        {!user && showInlineAuth && (
                          <div id="inline-auth-section-abo" className="bg-white border-2 border-[#003264] rounded-xl p-5 space-y-4" data-testid="inline-auth-form-abo">
                            <div className="flex items-center gap-3 mb-2">
                              <div className="w-10 h-10 bg-[#003264] rounded-full flex items-center justify-center">
                                <User className="w-5 h-5 text-white" />
                              </div>
                              <div>
                                <h3 className="font-bold text-[#003264]">
                                  {isReturningUser ? "Connexion rapide" : "Dernière étape — vos coordonnées"}
                                </h3>
                                <p className="text-xs text-gray-500">
                                  {isReturningUser ? "Connectez-vous pour finaliser" : "Votre compte est créé automatiquement"}
                                </p>
                              </div>
                            </div>

                            {!isReturningUser && (
                              <>
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <Label className="text-gray-700 text-sm">Prénom *</Label>
                                    <Input value={authData.prenom} onChange={(e) => setAuthData(p => ({...p, prenom: e.target.value}))} placeholder="Jean" className="mt-1 h-11" data-testid="inline-abo-prenom" />
                                  </div>
                                  <div>
                                    <Label className="text-gray-700 text-sm">Nom *</Label>
                                    <Input value={authData.nom} onChange={(e) => setAuthData(p => ({...p, nom: e.target.value}))} placeholder="Dupont" className="mt-1 h-11" data-testid="inline-abo-nom" />
                                  </div>
                                </div>
                                <div>
                                  <Label className="text-gray-700 text-sm">Téléphone *</Label>
                                  <div className="relative mt-1">
                                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    <Input value={authData.telephone} onChange={(e) => setAuthData(p => ({...p, telephone: e.target.value}))} placeholder="06 12 34 56 78" className="pl-10 h-11" data-testid="inline-abo-telephone" />
                                  </div>
                                </div>
                              </>
                            )}
                            
                            <div>
                              <Label className="text-gray-700 text-sm">Email *</Label>
                              <div className="relative mt-1">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <Input type="email" value={authData.email} onChange={(e) => setAuthData(p => ({...p, email: e.target.value}))} placeholder="jean.dupont@email.com" className="pl-10 h-11" data-testid="inline-abo-email" />
                              </div>
                            </div>
                            
                            <div>
                              <Label className="text-gray-700 text-sm">Mot de passe *</Label>
                              <div className="relative mt-1">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <Input type={showPassword ? "text" : "password"} value={authData.password} onChange={(e) => setAuthData(p => ({...p, password: e.target.value}))} placeholder="Min. 6 caractères" className="pl-10 pr-10 h-11" data-testid="inline-abo-password" />
                                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                              </div>
                            </div>

                            <div className="bg-gray-50 rounded-lg p-3 flex items-center gap-2 text-sm text-gray-600">
                              <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
                              <span>{adresse}, {codePostal} {ville}</span>
                              <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 ml-auto" />
                            </div>
                            
                            <Button onClick={handleInlineRegisterAndSubmit} disabled={loading}
                              className="w-full bg-[#003264] hover:bg-[#002244] text-white py-6 text-base" data-testid="btn-inline-abo-submit">
                              {loading ? "Création du compte et abonnement..." : (
                                <>{isReturningUser ? "Se connecter et m'abonner" : "Créer mon compte et m'abonner"}<ArrowRight className="w-5 h-5 ml-2" /></>
                              )}
                            </Button>

                            <button onClick={() => setIsReturningUser(!isReturningUser)} className="w-full text-center text-sm text-gray-500 hover:text-[#003264] transition" data-testid="toggle-abo-auth-mode">
                              {isReturningUser ? <>Pas encore de compte ? <span className="font-semibold text-[#003264]">Créer un compte</span></> : <>Déjà un compte ? <span className="font-semibold text-[#003264]">Se connecter</span></>}
                            </button>
                          </div>
                        )}

                        {(user || !showInlineAuth) && (
                          <>
                            <div className="flex gap-3">
                              <Button variant="outline" onClick={() => setStep(1)} className="flex-1">
                                <ArrowLeft className="w-4 h-4 mr-2" /> Retour
                              </Button>
                              <Button onClick={handleSubmit} disabled={loading || !adresse || !heure || !codePostal}
                                className="flex-1 bg-[#003264] hover:bg-[#002244] text-white" data-testid="btn-abo-submit">
                                {loading ? "Création..." : (
                                  <>{user ? "Créer l'abonnement" : "M'abonner maintenant"}<ArrowRight className="w-4 h-4 ml-2" /></>
                                )}
                              </Button>
                            </div>
                            
                            {!user && (
                              <p className="text-xs text-center text-gray-500">
                                Vous créerez votre compte à l'étape suivante — c'est rapide !
                              </p>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Cancel button */}
              {user && mesAbonnements.length > 0 && (
                <button
                  onClick={() => { setShowForm(false); setStep(1); setSelectedService(null); }}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Annuler
                </button>
              )}
            </div>

            {/* Sidebar - Price summary */}
            <div className="space-y-6">
              {price && (
                <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden sticky top-24">
                  <div className="p-6">
                    <h3 className="font-bold text-[#003264] mb-4">Récapitulatif</h3>
                    
                    <div className="space-y-3 mb-4">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Tarif unitaire</span>
                        <span className="font-medium">{price.tarifUnitaire}€/{menageMode === "m2" ? "m²" : "h"}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Prix / prestation</span>
                        <span className="text-lg font-bold text-gray-400 line-through">{price.prix}€</span>
                      </div>
                      {price.reduction > 0 && (
                        <div className="flex justify-between text-emerald-600">
                          <span>Économie abonnement</span>
                          <span className="font-medium">-{price.economie}€</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="bg-emerald-50 border-t-2 border-emerald-200 p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <BadgePercent className="w-4 h-4 text-emerald-600" />
                      <span className="text-xs font-bold text-emerald-700 uppercase">Avance immédiate</span>
                    </div>
                    <div className="flex items-baseline justify-between">
                      <span className="text-sm text-emerald-700">Vous payez</span>
                      <span className="text-2xl font-black text-emerald-600">{price.prixApresCredit}€</span>
                    </div>
                    <p className="text-xs text-emerald-600 mt-1">
                      L'État verse directement les {price.prixApresCredit}€ restants
                    </p>
                  </div>

                  {price.reduction > 0 && (
                    <div className="p-4 bg-gray-50 border-t text-center">
                      <p className="text-sm text-emerald-700">
                        <span className="font-bold">-{price.reduction}%</span> grâce à l'abonnement {frequences.find(f => f.value === frequence)?.label.toLowerCase()}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Info for non-logged users */}
              {!user && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                      <BadgePercent className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#003264] text-sm">Avance immédiate</h3>
                      <p className="text-xs text-emerald-600">Payez seulement 50% !</p>
                    </div>
                  </div>
                  <ul className="text-xs text-gray-600 space-y-1">
                    <li className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-emerald-500" /> Crédit d'impôt déduit automatiquement</li>
                    <li className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-emerald-500" /> Pause/reprise à tout moment</li>
                    <li className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-emerald-500" /> Facturation mensuelle</li>
                  </ul>
                </div>
              )}

              {/* Parrainage - only if logged */}
              {user && (
                <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-amber-200 flex items-center justify-center">
                      <Gift className="w-5 h-5 text-amber-700" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 text-sm">Parrainez vos proches</h3>
                      <p className="text-xs text-gray-600">Gagnez <strong className="text-amber-600">15€</strong> par filleul</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 bg-white rounded-lg p-2">
                    <span className="font-mono font-bold text-[#003264] flex-1 text-center text-sm">
                      {user.code_parrainage}
                    </span>
                    <button 
                      onClick={() => {
                        navigator.clipboard.writeText(user.code_parrainage);
                        toast.success("Code copié !");
                      }} 
                      className="p-2 hover:bg-gray-100 rounded"
                    >
                      <Copy className="w-4 h-4 text-gray-500" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          /* Liste des abonnements - only shown for logged users */
          user && (
            <div className="space-y-6">
              {/* Abonnements actifs */}
              <div>
                <h2 className="text-lg font-bold text-[#003264] mb-4 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                  Abonnements actifs ({abonnementsActifs.length})
                </h2>
                
                {abonnementsActifs.length === 0 ? (
                  <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center">
                    <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                      <Repeat className="w-8 h-8 text-gray-400" />
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-2">Aucun abonnement actif</h3>
                    <p className="text-gray-500 mb-4">Créez votre premier abonnement et bénéficiez de tarifs préférentiels</p>
                    <Button onClick={() => setShowForm(true)} className="bg-[#003264] hover:bg-[#002244] text-white">
                      Créer un abonnement
                    </Button>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-4">
                    {abonnementsActifs.map(abo => (
                      <div key={abo.id} className="bg-white rounded-2xl border border-gray-200 overflow-hidden card-hover">
                        <div className="p-5">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-xl bg-[#003264]/10 flex items-center justify-center">
                                <Home className="w-5 h-5 text-[#003264]" />
                              </div>
                              <div>
                                <p className="font-semibold text-gray-900 capitalize">{abo.type_prestation}</p>
                                <p className="text-sm text-gray-500 capitalize">{abo.frequence.replace("_", " ")}</p>
                              </div>
                            </div>
                            <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full">
                              -{abo.reduction_pourcent}%
                            </span>
                          </div>
                          
                          <div className="bg-gray-50 rounded-lg p-3 mb-3">
                            <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                              <CalendarDays className="w-4 h-4" />
                              <span>Prochaine intervention</span>
                            </div>
                            <p className="font-semibold text-[#003264]">
                              {new Date(abo.prochaine_intervention).toLocaleDateString('fr-FR', { 
                                weekday: 'long', day: 'numeric', month: 'long' 
                              })} à {abo.heure}
                            </p>
                          </div>

                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-500">Prix / prestation</span>
                            <div className="text-right">
                              <span className="font-bold text-[#003264]">{abo.prix_prestation}€</span>
                              <span className="text-emerald-600 ml-2">({abo.prix_apres_credit}€ après crédit)</span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="px-5 py-3 bg-gray-50 border-t flex gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handlePause(abo.id)}
                            className="flex-1 text-gray-600"
                          >
                            <Pause className="w-4 h-4 mr-1" /> Pause
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleAnnuler(abo.id)}
                            className="flex-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4 mr-1" /> Annuler
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Abonnements en pause */}
              {abonnementsPause.length > 0 && (
                <div>
                  <h2 className="text-lg font-bold text-gray-600 mb-4 flex items-center gap-2">
                    <Pause className="w-5 h-5 text-amber-500" />
                    En pause ({abonnementsPause.length})
                  </h2>
                  <div className="grid md:grid-cols-2 gap-4">
                    {abonnementsPause.map(abo => (
                      <div key={abo.id} className="bg-white rounded-2xl border border-gray-200 overflow-hidden opacity-75">
                        <div className="p-5">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center">
                              <Home className="w-5 h-5 text-gray-400" />
                            </div>
                            <div>
                              <p className="font-semibold text-gray-700 capitalize">{abo.type_prestation}</p>
                              <p className="text-sm text-gray-400 capitalize">{abo.frequence.replace("_", " ")}</p>
                            </div>
                          </div>
                        </div>
                        <div className="px-5 py-3 bg-gray-50 border-t">
                          <Button 
                            size="sm" 
                            onClick={() => handleReprendre(abo.id)}
                            className="w-full bg-[#003264] hover:bg-[#002244] text-white"
                          >
                            <Play className="w-4 h-4 mr-1" /> Reprendre
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )
        )}
      </div>
    </div>
  );
};
